import React, { useState, useMemo } from 'react';
import { useImageUpload } from './useImageUpload';
import {
  ShellFrame, SelectStep, CropStep, UploadingStep, PreviewPane,
  ToolSection, RecropSection, OrientationSection, MetaSection, ShellFooter
} from './ImageUploadParts';

/**
 * PictureUploadModal — the uploader for PHOTOGRAPHS.
 *
 * Opaque JPEG output, free-form crop, and no transparency tooling: background removal and colour
 * replace are meaningless on a photo, and JPEG carries no alpha to remove a background into.
 * Brand marks (logos, icons) use LogoUploadModal instead.
 *
 * Pipeline and shared chrome come from useImageUpload / ImageUploadParts. What lives here is the
 * quality control and the opaque-output configuration.
 */

// Bounding box applied when the caller sets no size constraint of its own. Because it bounds both
// axes, it caps the LONG edge whichever way round the photo is — a portrait shot of a tall panel
// is limited the same as a landscape one.
const DEFAULT_MAX_EDGE = 1920;

// Transparent pixels must be composited onto something before a JPEG encode, or canvas puts them
// on black. White matches the app's light theme and how photos read in a report.
const MATTE = '#ffffff';

const PictureUploadModal = ({
  isOpen, onClose, onUpload,
  title = 'Upload Picture',
  suggestedName = '',
  aspectRatio,          // undefined = free-form crop, which is the default for photos
  outputSize,
  maxOutputHeight,
  maxOutputWidth,
  maxSizeMB = 5,
  allowedTypes = '.png,.jpg,.jpeg,.webp',
  defaultQuality = 0.85,
}) => {
  const [quality, setQuality] = useState(defaultQuality);

  const unconstrained = !maxOutputWidth && !maxOutputHeight;
  const effMaxWidth = unconstrained ? DEFAULT_MAX_EDGE : maxOutputWidth;
  const effMaxHeight = unconstrained ? DEFAULT_MAX_EDGE : maxOutputHeight;

  const outputFormat = useMemo(() => ({
    type: 'image/jpeg',
    extension: 'jpg',
    label: `JPEG ${Math.round(quality * 100)}%`,
    quality,
    matte: MATTE,
  }), [quality]);

  const u = useImageUpload({
    isOpen, onClose, onUpload, suggestedName,
    aspectRatio, outputSize,
    maxOutputHeight: effMaxHeight,
    maxOutputWidth: effMaxWidth,
    maxSizeMB, allowedTypes,
    outputFormat,
    onReset: () => setQuality(defaultQuality),
  });

  if (!isOpen) return null;

  return (
    <ShellFrame title={title} onClose={onClose} error={u.error} footer={<ShellFooter u={u} onClose={onClose} />}>
      {u.step === 'select' && (
        <SelectStep
          u={u} maxSizeMB={maxSizeMB} allowedTypes={allowedTypes}
          specText={<>Image will be saved as <strong>JPEG</strong></>}
        />
      )}

      {u.step === 'crop' && u.sourceDataUrl && (
        <CropStep u={u} aspectRatio={aspectRatio}
          hint={aspectRatio
            ? <>Drag the handles to crop, then click <strong>Apply Crop</strong> to continue</>
            : <>Drag to crop, or click <strong>Apply Crop</strong> to keep the whole picture</>} />
      )}

      {u.step === 'edit' && (
        <div className="stui-imgupload-edit">
          {/* opaque: a checkerboard behind a JPEG would imply transparency it cannot carry */}
          <PreviewPane u={u} opaque />

          <div className="stui-imgupload-toolbar">
            <RecropSection u={u} />

            {/* 2. Quality */}
            <ToolSection num={2} done={quality !== defaultQuality} label="Quality">
              <div className="stui-imgupload-tolerance-desc">
                Higher keeps more detail in fine text and edges, at a larger file size
              </div>
              <div className="stui-imgupload-range-row">
                <span className="stui-imgupload-range-end">Smaller</span>
                <input
                  type="range" min={50} max={100} step={1}
                  value={Math.round(quality * 100)}
                  onChange={e => setQuality(parseInt(e.target.value) / 100)}
                />
                <span className="stui-imgupload-range-end">Sharper</span>
              </div>
            </ToolSection>

            <OrientationSection u={u} num={3} />
            <MetaSection u={u} maxSizeMB={maxSizeMB} formatLabel={outputFormat.label} />
          </div>
        </div>
      )}

      {u.step === 'uploading' && <UploadingStep />}
    </ShellFrame>
  );
};

export default PictureUploadModal;
